import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { getBankAccounts, createBankAccount, updateBankAccount, deleteBankAccount, getBankTransactions, createBankTransaction } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Plus,
  Trash,
  PencilSimple,
  Bank,
  ArrowUp,
  ArrowDown,
  ArrowsLeftRight,
  CreditCard,
  Star,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const TYPE_COLORS = {
  deposito: 'bg-emerald-500/20 text-emerald-400',
  retiro: 'bg-red-500/20 text-red-400',
  transferencia: 'bg-blue-500/20 text-blue-400',
};

export function BankPage() {
  const { t } = useLanguage();
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [loading, setLoading] = useState(true);
  const [isAccountDialogOpen, setIsAccountDialogOpen] = useState(false);
  const [isTxDialogOpen, setIsTxDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [accountForm, setAccountForm] = useState({
    account_name: '', bank_name: '', account_number: '', account_type: 'corriente', currency: 'USD', initial_balance: 0, notes: '', is_primary: false,
  });
  const [txForm, setTxForm] = useState({
    bank_account_id: '', transaction_type: 'deposito', amount: 0, date: new Date().toISOString().split('T')[0], description: '', reference: '',
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAccounts(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (selectedAccount) fetchTransactions(); }, [selectedAccount]);

  const fetchAccounts = async () => {
    try {
      const res = await getBankAccounts();
      setAccounts(res.data);
      if (res.data.length > 0 && !selectedAccount) setSelectedAccount(res.data[0]._id);
    } catch { toast.error(t('errorOccurred')); }
    finally { setLoading(false); }
  };

  const fetchTransactions = async () => {
    try {
      const res = await getBankTransactions({ bank_account_id: selectedAccount });
      setTransactions(res.data);
    } catch { toast.error(t('errorOccurred')); }
  };

  const handleAccountSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAccount) { await updateBankAccount(editingAccount._id, accountForm); }
      else { await createBankAccount(accountForm); }
      toast.success(t('successSaved'));
      setIsAccountDialogOpen(false); resetAccountForm(); fetchAccounts();
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  const handleTxSubmit = async (e) => {
    e.preventDefault();
    try {
      await createBankTransaction({ ...txForm, bank_account_id: selectedAccount });
      toast.success('Transacción registrada');
      setIsTxDialogOpen(false); resetTxForm(); fetchAccounts(); fetchTransactions();
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  const handleDeleteAccount = async (id) => {
    if (window.confirm('Eliminar esta cuenta bancaria?')) {
      try { await deleteBankAccount(id); toast.success(t('successDeleted')); fetchAccounts(); }
      catch { toast.error(t('errorOccurred')); }
    }
  };

  const handleEditAccount = (acc) => {
    setEditingAccount(acc);
    setAccountForm({ account_name: acc.account_name, bank_name: acc.bank_name, account_number: acc.account_number, account_type: acc.account_type, currency: acc.currency || 'USD', initial_balance: acc.current_balance, notes: acc.notes || '', is_primary: acc.is_primary || false });
    setIsAccountDialogOpen(true);
  };

  const resetAccountForm = () => { setEditingAccount(null); setAccountForm({ account_name: '', bank_name: '', account_number: '', account_type: 'corriente', currency: 'USD', initial_balance: 0, notes: '', is_primary: false }); };
  const resetTxForm = () => { setTxForm({ bank_account_id: '', transaction_type: 'deposito', amount: 0, date: new Date().toISOString().split('T')[0], description: '', reference: '' }); };

  const totalBalance = accounts.reduce((s, a) => s + (a.current_balance || 0), 0);
  const currentAccount = accounts.find(a => a._id === selectedAccount);

  return (
    <Layout title="Banca">
      <div className="space-y-6" data-testid="bank-page">
        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="valhalla-card p-5 border-[#E2B05E]/30 md:col-span-1">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-[#E2B05E]/10 text-[#E2B05E]"><Bank weight="duotone" className="w-6 h-6" /></div>
              <div>
                <p className="text-xs text-[#9CA3AF] uppercase tracking-wider">Balance Total</p>
                <p className="font-['Outfit'] text-2xl font-bold text-[#E2B05E]">${totalBalance.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="valhalla-card p-5 md:col-span-1">
            <p className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-1">Cuentas Registradas</p>
            <p className="font-['Outfit'] text-2xl font-bold text-[#F3F4F6]">{accounts.length}</p>
          </div>
          <div className="valhalla-card p-5 md:col-span-1 flex items-center justify-end gap-3">
            <Dialog open={isAccountDialogOpen} onOpenChange={(o) => { setIsAccountDialogOpen(o); if (!o) resetAccountForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#E2B05E] hover:bg-[#F0C47A] text-[#0B0E14] font-semibold" data-testid="new-account-btn"><Plus className="w-4 h-4 mr-2" /> Nueva Cuenta</Button>
              </DialogTrigger>
              <DialogContent className="bg-[#151B26] border-[#242E42] max-w-md">
                <DialogHeader><DialogTitle className="text-[#F3F4F6]">{editingAccount ? 'Editar Cuenta' : 'Nueva Cuenta Bancaria'}</DialogTitle></DialogHeader>
                <form onSubmit={handleAccountSubmit} className="space-y-4">
                  <div className="space-y-2"><Label className="text-[#9CA3AF]">Nombre de la Cuenta</Label><Input value={accountForm.account_name} onChange={e => setAccountForm({...accountForm, account_name: e.target.value})} className="valhalla-input" required /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label className="text-[#9CA3AF]">Banco</Label><Input value={accountForm.bank_name} onChange={e => setAccountForm({...accountForm, bank_name: e.target.value})} className="valhalla-input" required /></div>
                    <div className="space-y-2"><Label className="text-[#9CA3AF]">No. Cuenta</Label><Input value={accountForm.account_number} onChange={e => setAccountForm({...accountForm, account_number: e.target.value})} className="valhalla-input" required /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#9CA3AF]">Tipo</Label>
                      <Select value={accountForm.account_type} onValueChange={v => setAccountForm({...accountForm, account_type: v})}>
                        <SelectTrigger className="valhalla-input"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#151B26] border-[#242E42]">
                          <SelectItem value="corriente">Corriente</SelectItem>
                          <SelectItem value="ahorros">Ahorros</SelectItem>
                          <SelectItem value="empresarial">Empresarial</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2"><Label className="text-[#9CA3AF]">Balance Inicial</Label><Input type="number" step="0.01" value={accountForm.initial_balance} onChange={e => setAccountForm({...accountForm, initial_balance: parseFloat(e.target.value)||0})} className="valhalla-input" /></div>
                  </div>
                  <div className="space-y-2"><Label className="text-[#9CA3AF]">Notas</Label><Textarea value={accountForm.notes} onChange={e => setAccountForm({...accountForm, notes: e.target.value})} className="valhalla-input" /></div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-[#242E42]">
                    <Button type="button" variant="outline" onClick={() => setIsAccountDialogOpen(false)} className="border-[#242E42] text-[#9CA3AF]">{t('cancel')}</Button>
                    <Button type="submit" className="bg-[#E2B05E] hover:bg-[#F0C47A] text-[#0B0E14]">{t('save')}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Account Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {accounts.map(acc => (
            <div key={acc._id} onClick={() => setSelectedAccount(acc._id)}
              className={`valhalla-card p-5 cursor-pointer transition-all ${selectedAccount === acc._id ? 'border-[#3AB8D6] ring-1 ring-[#3AB8D6]/30' : 'hover:border-[#242E42]'}`}
              data-testid={`bank-card-${acc._id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-[#3AB8D6]" />
                  <h4 className="font-semibold text-[#F3F4F6]">{acc.account_name}</h4>
                  {acc.is_primary && <Star weight="fill" className="w-4 h-4 text-[#E2B05E]" />}
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleEditAccount(acc); }} className="text-[#9CA3AF] hover:text-[#3AB8D6] h-7 w-7 p-0"><PencilSimple className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDeleteAccount(acc._id); }} className="text-[#9CA3AF] hover:text-red-400 h-7 w-7 p-0"><Trash className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
              <p className="text-xs text-[#9CA3AF] mb-1">{acc.bank_name} | {acc.account_type}</p>
              <p className="text-xs text-[#9CA3AF] mb-3">****{acc.account_number?.slice(-4)}</p>
              <p className="font-['Outfit'] text-xl font-bold text-[#F3F4F6]">${acc.current_balance?.toLocaleString()}</p>
            </div>
          ))}
        </div>

        {/* Transactions */}
        {selectedAccount && (
          <div className="valhalla-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-['Outfit'] text-lg font-semibold text-[#F3F4F6]">
                Movimientos — {currentAccount?.account_name}
              </h3>
              <Dialog open={isTxDialogOpen} onOpenChange={(o) => { setIsTxDialogOpen(o); if (!o) resetTxForm(); }}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-[#3AB8D6] hover:bg-[#4AD5F6] text-[#0B0E14] font-semibold" data-testid="new-tx-btn"><Plus className="w-4 h-4 mr-2" /> Nuevo Movimiento</Button>
                </DialogTrigger>
                <DialogContent className="bg-[#151B26] border-[#242E42] max-w-md">
                  <DialogHeader><DialogTitle className="text-[#F3F4F6]">Registrar Movimiento</DialogTitle></DialogHeader>
                  <form onSubmit={handleTxSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-[#9CA3AF]">Tipo</Label>
                      <Select value={txForm.transaction_type} onValueChange={v => setTxForm({...txForm, transaction_type: v})}>
                        <SelectTrigger className="valhalla-input"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#151B26] border-[#242E42]">
                          <SelectItem value="deposito">Depósito</SelectItem>
                          <SelectItem value="retiro">Retiro</SelectItem>
                          <SelectItem value="transferencia">Transferencia</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2"><Label className="text-[#9CA3AF]">Monto ($)</Label><Input type="number" step="0.01" value={txForm.amount} onChange={e => setTxForm({...txForm, amount: parseFloat(e.target.value)||0})} className="valhalla-input" required /></div>
                      <div className="space-y-2"><Label className="text-[#9CA3AF]">Fecha</Label><Input type="date" value={txForm.date} onChange={e => setTxForm({...txForm, date: e.target.value})} className="valhalla-input" required /></div>
                    </div>
                    <div className="space-y-2"><Label className="text-[#9CA3AF]">Descripción</Label><Input value={txForm.description} onChange={e => setTxForm({...txForm, description: e.target.value})} className="valhalla-input" required /></div>
                    <div className="space-y-2"><Label className="text-[#9CA3AF]">Referencia</Label><Input value={txForm.reference} onChange={e => setTxForm({...txForm, reference: e.target.value})} className="valhalla-input" placeholder="No. cheque, transferencia, etc." /></div>
                    <div className="flex justify-end gap-3 pt-4 border-t border-[#242E42]">
                      <Button type="button" variant="outline" onClick={() => setIsTxDialogOpen(false)} className="border-[#242E42] text-[#9CA3AF]">{t('cancel')}</Button>
                      <Button type="submit" className="bg-[#3AB8D6] hover:bg-[#4AD5F6] text-[#0B0E14]">{t('save')}</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {transactions.length > 0 ? (
              <table className="data-table">
                <thead><tr><th>Fecha</th><th>Tipo</th><th>Descripción</th><th>Referencia</th><th>Monto</th></tr></thead>
                <tbody>
                  {transactions.map(tx => (
                    <tr key={tx._id}>
                      <td className="text-[#9CA3AF]">{tx.date}</td>
                      <td><Badge className={TYPE_COLORS[tx.transaction_type]}>{tx.transaction_type === 'deposito' ? 'Depósito' : tx.transaction_type === 'retiro' ? 'Retiro' : 'Transferencia'}</Badge></td>
                      <td className="text-[#F3F4F6]">{tx.description}</td>
                      <td className="text-[#9CA3AF]">{tx.reference || '-'}</td>
                      <td className={`font-['Outfit'] font-bold ${tx.transaction_type === 'deposito' ? 'text-emerald-400' : 'text-red-400'}`}>
                        {tx.transaction_type === 'deposito' ? '+' : '-'}${tx.amount?.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-[#9CA3AF] text-center py-8">No hay movimientos registrados</p>
            )}
          </div>
        )}

        {accounts.length === 0 && !loading && (
          <div className="valhalla-card p-12 text-center">
            <Bank className="w-12 h-12 mx-auto text-[#9CA3AF] mb-4" />
            <p className="text-[#9CA3AF]">No hay cuentas bancarias registradas</p>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default BankPage;
